import React, { useState, useRef, useEffect } from "react";
import { HiBars3, HiInbox, HiChatBubbleLeftRight } from "react-icons/hi2";
import { HiOutlineUserCircle, HiOutlineKey, HiOutlineLogout } from "react-icons/hi";
import api from "../api";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

function Header({ user, setPage, setUser, setSidebarOpen, onLogout }) {
  const [open, setOpen] = useState(false);
  const [userInboxDot, setUserInboxDot] = useState(false);
  const [managerApprovalsDot, setManagerApprovalsDot] = useState(false);
  const [managerRequestsDot, setManagerRequestsDot] = useState(false);
  const [adminInboxDot, setAdminInboxDot] = useState(false);
  const ref = useRef();

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const getLatestTime = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;

      let latest = null;

      rows.forEach((row) => {
        const raw = row?.updated_at || row?.created_at || null;
        if (!raw) return;

        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return;

        if (!latest || d > latest) latest = d;
      });

      return latest;
    };

    const hasUnreadFromStorage = (rows, key) => {
      const latest = getLatestTime(rows);
      if (!latest) return false;

      const seen = localStorage.getItem(key);
      if (!seen) return true;

      const seenDate = new Date(seen);
      if (Number.isNaN(seenDate.getTime())) return true;

      return latest > seenDate;
    };

    const loadNotifications = async () => {
      if (!user?.id) {
        if (!cancelled) {
          setUserInboxDot(false);
          setManagerApprovalsDot(false);
          setManagerRequestsDot(false);
          setAdminInboxDot(false);
        }
        return;
      }

      try {
        if (user.role === "user") {
          const [requestRes, appreciationRes] = await Promise.all([
            api.get(`/requests/user/${user.id}`),
            api.get(`/appreciations/user/${user.id}`)
          ]);

          const requestUnread = hasUnreadFromStorage(
            requestRes.data,
            `user_inbox_seen_${user.id}`
          );
          const appreciationUnread = hasUnreadFromStorage(
            appreciationRes.data,
            `user_inbox_seen_${user.id}`
          );

          if (!cancelled) {
            setUserInboxDot(requestUnread || appreciationUnread);
          }
        }

        if (user.role === "manager") {
          const [approvalsRes, managerReqRes] = await Promise.all([
            api.get(`/requests/manager/${user.id}`),
            api.get(`/manager-requests/manager/${user.id}`)
          ]);

          if (!cancelled) {
            setManagerApprovalsDot(
              hasUnreadFromStorage(
                approvalsRes.data,
                `manager_approvals_seen_${user.id}`
              )
            );
            setManagerRequestsDot(
              hasUnreadFromStorage(
                managerReqRes.data,
                `manager_requests_seen_${user.id}`
              )
            );
          }
        }

        if (user.role === "admin") {
          const adminInboxRes = await api.get("/manager-requests");

          if (!cancelled) {
            setAdminInboxDot(
              hasUnreadFromStorage(
                adminInboxRes.data,
                `admin_inbox_seen_${user.id}`
              )
            );
          }
        }
      } catch (err) {
        console.error("Header notification load failed:", err);
      }
    };

    loadNotifications();

    const interval = setInterval(loadNotifications, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const goToPage = (page) => {
    if (user?.id) {
      if (page === "inbox") {
        localStorage.setItem(`user_inbox_seen_${user.id}`, new Date().toISOString());
        setUserInboxDot(false);
      }

      if (page === "managerInbox") {
        localStorage.setItem(
          `manager_approvals_seen_${user.id}`,
          new Date().toISOString()
        );
        setManagerApprovalsDot(false);
      }

      if (page === "managerRequests") {
        localStorage.setItem(
          `manager_requests_seen_${user.id}`,
          new Date().toISOString()
        );
        setManagerRequestsDot(false);
      }

      if (page === "adminInbox") {
        localStorage.setItem(`admin_inbox_seen_${user.id}`, new Date().toISOString());
        setAdminInboxDot(false);
      }
    }

    setPage(page);
    setOpen(false);
  };

  const handleLogout = () => {
    setOpen(false);

    if (onLogout) {
      onLogout();
      return;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <div style={header}>
      <div style={leftSection}>
        <IconButton
          label="Menu"
          onClick={() => setSidebarOpen((prev) => !prev)}
        >
          <HiBars3 />
        </IconButton>

        {user?.role === "user" && (
          <>
            <IconButton
              label="Requests"
              onClick={() => goToPage("requests")}
            >
              <HiChatBubbleLeftRight />
            </IconButton>

            <IconButton
              label="Inbox"
              onClick={() => goToPage("inbox")}
              showDot={userInboxDot}
            >
              <HiInbox />
            </IconButton>
          </>
        )}

        {user?.role === "manager" && (
          <>
            <IconButton
              label="Approvals"
              onClick={() => goToPage("managerInbox")}
              showDot={managerApprovalsDot}
            >
              <HiInbox />
            </IconButton>

            <IconButton
              label="Requests"
              onClick={() => goToPage("managerRequests")}
              showDot={managerRequestsDot}
            >
              <HiChatBubbleLeftRight />
            </IconButton>
          </>
        )}

        {user?.role === "admin" && (
          <IconButton
            label="Inbox"
            onClick={() => goToPage("adminInbox")}
            showDot={adminInboxDot}
          >
            <HiInbox />
          </IconButton>
        )}
      </div>

      <div ref={ref} style={{ position: "relative" }}>
        <div style={profileTrigger} onClick={() => setOpen(!open)}>
          <div style={avatar}>
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>

          <div style={profileTextWrap}>
            <span style={profileName}>{user?.name || "User"}</span>
          </div>

          <span style={chevron}>{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div style={dropdown}>
            <div style={dropdownHeader}>
              <div style={dropdownAvatar}>
                {(user?.name || "U").charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={dropdownName}>{user?.name || "User"}</div>
                <div style={dropdownEmail}>{user?.email || "No email"}</div>
              </div>
            </div>

            <div style={dropdownMenu}>
              <button style={menuBtn} onClick={() => goToPage("profile")}>
                <HiOutlineUserCircle style={menuIcon} />
                Profile
              </button>

              <button style={menuBtn} onClick={() => goToPage("reset")}>
                <HiOutlineKey style={menuIcon} />
                Reset Password
              </button>

              <button
                style={logoutBtn}
                onClick={handleLogout}
              >
                <HiOutlineLogout style={menuIcon} />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IconButton({ children, onClick, label, showDot = false }) {
  return (
    <button type="button" onClick={onClick} title={label} style={iconButton}>
      <span style={iconInner}>{children}</span>
      {showDot && <span style={notificationDot} />}
    </button>
  );
}

const header = {
  height: "68px",
  background: "linear-gradient(135deg, #0F766E 0%, #115E59 100%)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0 20px",
  color: "#FFFFFF",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  boxShadow: "0 8px 24px rgba(15,118,110,0.25)"
};

const leftSection = {
  display: "flex",
  alignItems: "center",
  gap: "10px"
};

const iconButton = {
  width: "40px",
  height: "40px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.18)",
  background: "rgba(255,255,255,0.10)",
  color: "#ECFEFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "20px",
  transition: "all 0.2s ease",
  backdropFilter: "blur(6px)",
  position: "relative"
};

const iconInner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const notificationDot = {
  position: "absolute",
  top: "7px",
  right: "7px",
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background: "#EF4444",
  boxShadow: "0 0 0 2px rgba(15,118,110,0.45)"
};

const profileTrigger = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  background: "rgba(255,255,255,0.10)",
  border: "1px solid rgba(255,255,255,0.18)",
  padding: "7px 12px",
  borderRadius: "14px",
  cursor: "pointer",
  minWidth: "156px"
};

const avatar = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.20)",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  fontSize: "13px",
  flexShrink: 0
};

const profileTextWrap = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  flex: 1,
  minWidth: 0
};

const profileName = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#FFFFFF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const chevron = {
  fontSize: "10px",
  color: "rgba(255,255,255,0.85)"
};

const dropdown = {
  position: "absolute",
  top: "52px",
  right: "0",
  width: "250px",
  background: theme.surface,
  color: theme.text,
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
  border: `1px solid ${theme.border}`
};

const dropdownHeader = {
  display: "flex",
  gap: "12px",
  alignItems: "center",
  padding: "16px",
  background: theme.surfaceSoft,
  borderBottom: `1px solid ${theme.border}`
};

const dropdownAvatar = {
  width: "42px",
  height: "42px",
  borderRadius: "50%",
  background: theme.primary,
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold"
};

const dropdownName = {
  fontWeight: 700,
  fontSize: "14px",
  color: theme.text
};

const dropdownEmail = {
  fontSize: "12px",
  color: theme.textMuted,
  marginTop: "3px",
  wordBreak: "break-word"
};

const dropdownMenu = {
  padding: "10px"
};

const menuBtn = {
  width: "100%",
  border: "none",
  background: "transparent",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "12px 12px",
  borderRadius: "12px",
  cursor: "pointer",
  color: theme.text,
  fontSize: "14px",
  marginBottom: "6px",
  textAlign: "left"
};

const logoutBtn = {
  ...menuBtn,
  color: theme.danger,
  background: "#FEF2F2"
};

const menuIcon = {
  fontSize: "18px"
};

export default Header;